import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/hooks/use-organization";
import { unwrapFunctionInvoke, getFriendlyMessage, logError } from "@/lib/error-utils";

// SECURITY: Storage key now includes user ID to prevent data leakage between users
const STORAGE_KEY_PREFIX = "spotter-conversation-";

export interface AIMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolCalls?: {
    id: string;
    name: string;
    arguments: Record<string, unknown>;
  }[];
  toolResults?: {
    toolCallId: string;
    result: unknown;
    isError?: boolean;
  }[];
  isLoading?: boolean;
}

interface ChatResponse {
  message: {
    role: "assistant";
    content: string;
    toolCalls?: AIMessage["toolCalls"];
    toolResults?: AIMessage["toolResults"];
  };
  navigationRoute?: string;
}

export interface PageContext {
  path: string;
  pageTitle?: string;
  section?: string;
  params?: Record<string, string | undefined>;
}

// Get user-specific storage key
function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

// Load messages from sessionStorage for a specific user
function loadMessages(userId: string | null): AIMessage[] {
  if (!userId) return [];
  try {
    const stored = sessionStorage.getItem(getStorageKey(userId));
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((msg: AIMessage) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (e) {
    console.error("[useAIAgent] Failed to load messages from storage:", e);
  }
  return [];
}

// Save messages to sessionStorage for a specific user
function saveMessages(userId: string | null, messages: AIMessage[]) {
  if (!userId) return;
  try {
    // Don't save loading states
    const toSave = messages.filter(m => !m.isLoading);
    sessionStorage.setItem(getStorageKey(userId), JSON.stringify(toSave));
  } catch (e) {
    console.error("[useAIAgent] Failed to save messages to storage:", e);
  }
}

// Clear conversation for a specific user (exported for logout cleanup)
export function clearSpotterConversation(userId: string) {
  sessionStorage.removeItem(getStorageKey(userId));
}

export function useAIAgent(pageContext?: PageContext) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const navigate = useNavigate();
  const { organization } = useOrganization();

  // React to user changes (login/logout)
  useEffect(() => {
    if (user) {
      if (!initializedRef.current) {
        setMessages(loadMessages(user.id));
        initializedRef.current = true;
      }
    } else {
      setMessages([]);
      initializedRef.current = false;
    }
  }, [user?.id]);

  // Persist messages to sessionStorage whenever they change
  useEffect(() => {
    if (user?.id) {
      saveMessages(user.id, messages);
    }
  }, [messages, user?.id]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    setError(null);

    // Add user message
    const userMessage: AIMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      timestamp: new Date(),
    };

    // Add placeholder assistant message
    const assistantId = crypto.randomUUID();
    const assistantPlaceholder: AIMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
    setIsLoading(true);

    try {
      if (!user) {
        throw new Error("Not authenticated");
      }

      // Build conversation history for API
      const conversationHistory = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content: msg.content,
        toolCalls: msg.toolCalls,
        toolResults: msg.toolResults,
      }));

      // Call the edge function using supabase.functions.invoke
      const invokeResult = await supabase.functions.invoke<ChatResponse>("ai-agent", {
        body: {
          messages: conversationHistory,
          organizationId: organization?.id,
          pageContext: pageContext ? {
            path: pageContext.path,
            pageTitle: pageContext.pageTitle,
            section: pageContext.section,
          } : undefined,
        },
      });

      const data = unwrapFunctionInvoke<ChatResponse>(invokeResult);

      if (!data) {
        throw new Error("No response received");
      }

      // Update the placeholder with actual response
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: data.message.content,
                toolCalls: data.message.toolCalls,
                toolResults: data.message.toolResults,
                isLoading: false,
              }
            : msg
        )
      );

      // Handle navigation if requested
      if (data.navigationRoute) {
        // Small delay to let the message render
        setTimeout(() => {
          navigate(data.navigationRoute!);
        }, 500);
      }
    } catch (err) {
      // Capture for support; the chat bubble shows a friendly inline message
      // (this surface uses an inline error bubble, not a toast).
      const { code } = logError(err, "ai-agent-chat");
      const friendly = getFriendlyMessage(err);
      setError(friendly.userMessage);

      // Update placeholder with error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                content: `Sorry, I ran into a problem: ${friendly.userMessage} (code: ${code})`,
                isLoading: false,
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, navigate]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    setError(null);
    if (user?.id) {
      sessionStorage.removeItem(getStorageKey(user.id));
    }
  }, [user?.id]);

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    clearHistory,
  };
}
